/**
 * Onglet Présentation - Informations générales de l'entreprise
 * Inclut la sélection des lots techniques et zones d'intervention (prestataire libre)
 * FRONT-09: Affichage en lecture seule pour les comptes Accord Cadre
 * FRONT-24: Ajout section "Informations légales" avec données entreprise
 */
import React, { useState, useEffect, useRef } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';
import { useProfilType } from '../hooks/useProfilType';
import { useChampsManquants } from '../hooks/useChampsManquants';
import { useAutoSave } from '../../../hooks/useAutoSave';
import { useAutoSaveToasts } from '../../../hooks/useAutoSaveToasts';
import type { PresentationData, EntrepriseFormData, AdresseData } from '../../../services/profileService';
import { checkSiret } from '../../../services/profileService';
import {
  adresseUneLigne,
  adresseEntiteUneLigne,
  separerTypeVoie,
  estFrance,
  PAYS_EUROPE,
  PAYS_HORS_EUROPE,
} from '../../../utils/adresse';
import Button from '../../../composants/Button/Button';
import Switch from '../../../composants/Switch/Switch';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import PrestataireZonesCarte from '../components/PrestataireZonesCarte';
import { departementFromCodePostal } from '../../../utils/departement';
import { parseMontantFr, formatMontantInput, toNumber } from '../../../utils/montant';
import './TabPresentation.css';

// Interface pour les suggestions IGN (autocomplétion adresse)
interface IgnSuggestion {
  label: string;
  houseNumber?: string;
  street?: string;
  postcode?: string;
  city?: string;
}

// Formes juridiques disponibles
const FORMES_JURIDIQUES = [
  'SAS',
  'SARL',
  'SA',
  'EURL',
  'EI',
  'SASU',
  'SNC',
  'Auto-entrepreneur',
  'Association',
  'Autre',
];

const TabPresentation: React.FC = () => {
  const { profile, updatePresentation, updateEntreprise, updateSiret, updateLots, updateLotsForLotTechnique, isSaving, reportAutoSaveStatus } = useProfile();
  const profilType = useProfilType(); // FRONT-09: Pour conditionner l'affichage des lots
  const { classeChamp } = useChampsManquants();

  // SIRET : éditable si vide OU si l'utilisateur est un prestataire (EPIC-29 Story 29.2)
  const siretActuel = profile?.entreprise?.siret || profile?.entite?.siret || '';
  const siretIsEmpty = !siretActuel;
  const isPLAdmin = profilType.isPrestataireLibre && profilType.isAdmin;
  const isACAdmin = profilType.isAccordCadre && profilType.isAdmin;
  const canEditSiret = siretIsEmpty || profilType.isPrestataireLibre || profilType.isAccordCadre;
  const [siretInput, setSiretInput] = useState(isPLAdmin && siretActuel ? siretActuel : '');
  const [siretError, setSiretError] = useState<string | null>(null);
  const [siretChecking, setSiretChecking] = useState(false);
  const siretFieldRef = useRef<HTMLInputElement>(null);

  // Autocomplétion adresse IGN
  const [adresseInput, setAdresseInput] = useState('');
  const [adresseSuggestions, setAdresseSuggestions] = useState<IgnSuggestion[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const adresseUserTypingRef = useRef(false); // Flag : autocomplete uniquement si l'utilisateur tape

  // FRONT-24: Données entreprise pour l'édition
  const [entrepriseFormData, setEntrepriseFormData] = useState<EntrepriseFormData>({
    precision: '',
    RCS: '',
    statut_juridique: '',
    capital: null,
    date_creation: null,
    redressement_judiciaire: false,
    adresse: {
      numero_voie: '',
      type_voie: '',
      nom_voie: '',
      code_postal: '',
      ville: '',
      pays: 'France',
    },
  });

  // Le pays du siege commande tout le reste du bloc adresse : auto-completion IGN ou saisie eclatee,
  // gabarit du code postal, derivation du departement. La colonne `adresses.pays` melange des
  // libelles ("France", "Allemagne") et des codes ISO ("FR") selon le chemin qui l'a ecrite ;
  // `estFrance` absorbe les deux formes, une comparaison directe n'en couvrirait qu'une.
  const paysAdresse = entrepriseFormData.adresse?.pays || '';
  const adresseEstFrancaise = estFrance(paysAdresse);
  // Une valeur deja en base qui n'est pas un code ISO ("Allemagne") n'a pas d'option correspondante
  // dans la liste : sans cette option de repli, le select s'afficherait vide et la premiere
  // sauvegarde ecraserait la valeur existante.
  const paysEstCodeConnu = [...PAYS_EUROPE, ...PAYS_HORS_EUROPE]
    .some(p => p.code === paysAdresse.toUpperCase());

  const [presInitialized, setPresInitialized] = useState(false);
  const [entInitialized, setEntInitialized] = useState(false);
  const [formData, setFormData] = useState<PresentationData>({
    presentation_entreprise: '',
    presentation_operationnel: '',
    presentation_humain: '',
    presentation_environnement: '',
    ca_n1: null,
    part_ca_n1: null,
    ca_n2: null,
    part_ca_n2: null,
    ca_n3: null,
    part_ca_n3: null,
    rh_n1: null,
    part_rh_n1: null,
    rh_n2: null,
    part_rh_n2: null,
    rh_n3: null,
    part_rh_n3: null,
  });

  // Auto-redimensionnement des textareas de description : la hauteur suit le
  // contenu pour que les champs restent lisibles en entier, sans scroll interne.
  // Ref-callback : se rejoue a chaque rendu (frappe, chargement des donnees,
  // re-ouverture de la section pliable), donc la hauteur reste synchronisee.
  const fitTextarea = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // Années pour CA et effectifs (3 dernières années)
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear - 2, currentYear - 3];

  // Charger les données de présentation — se ré-exécute uniquement si profile.presentation change
  // (pas si profile.completion ou profile.entreprise change, évitant la ré-hydratation parasite)
  useEffect(() => {
    if (!profile?.presentation) return;
    const p = profile.presentation;
    setFormData({
      presentation_entreprise: p.presentation_entreprise || '',
      presentation_operationnel: p.presentation_operationnel || '',
      presentation_humain: p.presentation_humain || '',
      presentation_environnement: p.presentation_environnement || '',
      ca_n1: toNumber(p.ca_n1),
      part_ca_n1: p.part_ca_n1,
      ca_n2: toNumber(p.ca_n2),
      part_ca_n2: p.part_ca_n2,
      ca_n3: toNumber(p.ca_n3),
      part_ca_n3: p.part_ca_n3,
      rh_n1: p.rh_n1,
      part_rh_n1: p.part_rh_n1,
      rh_n2: p.rh_n2,
      part_rh_n2: p.part_rh_n2,
      rh_n3: p.rh_n3,
      part_rh_n3: p.part_rh_n3,
    });
    setPresInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.presentation]);

  // FRONT-24: Charger les données entreprise — se ré-exécute uniquement si profile.entreprise change
  // Cela garantit que la ré-hydratation n'écrase pas les saisies en cours lors d'une mise à jour
  // de profile.completion ou profile.presentation (les deux objets sont des références distinctes)
  useEffect(() => {
    if (!profile?.entreprise) return;
    const e = profile.entreprise;
    setEntrepriseFormData({
      precision: e.precision || '',
      RCS: e.RCS || '',
      statut_juridique: e.statut_juridique || '',
      capital: toNumber(e.capital),
      date_creation: e.date_creation,
      redressement_judiciaire: e.redressement_judiciaire || false,
      adresse: {
        numero_voie: e.adresse?.numero_voie || '',
        type_voie: e.adresse?.type_voie || '',
        nom_voie: e.adresse?.nom_voie || '',
        code_postal: e.adresse?.code_postal || '',
        ville: e.adresse?.ville || '',
        pays: e.adresse?.pays || 'France',
      },
    });

    // Initialiser l'input adresse avec l'adresse existante
    if (e.adresse) {
      const a = e.adresse;
      // Ligne complete (voie, code postal ville, pays) : c'est le seul champ adresse affiche, il doit
      // donc porter toute la localisation. L'ancienne composition collait le numero au nom de voie
      // avec une virgule et laissait tomber type_voie et pays.
      const adresseComplete = adresseEntiteUneLigne(a);
      adresseUserTypingRef.current = false; // Reset : pas d'autocomplete au chargement
      setAdresseInput(adresseComplete);
    }

    // EPIC-29 Story 29.2 : Pré-remplir le SIRET pour tous les prestataires
    if (profilType.isPrestataireLibre || profilType.isAccordCadre) {
      const siret = e.siret || profile?.entite?.siret || '';
      if (siret) setSiretInput(siret);
    }
    setEntInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.entreprise]);

  // Autocomplétion adresse IGN
  useEffect(() => {
    // Le geocodeur IGN ne couvre que le territoire francais : interroge sur une adresse etrangere,
    // il propose l'adresse francaise au libelle le plus proche. Hors de France, la saisie est donc
    // eclatee champ par champ et l'auto-completion est coupee, pas seulement masquee.
    if (!adresseEstFrancaise) {
      setAdresseSuggestions([]);
      return;
    }

    // Autocomplete uniquement si l'utilisateur tape activement dans le champ
    if (!adresseUserTypingRef.current) {
      return;
    }

    if (adresseInput.trim().length < 3) {
      setAdresseSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();

      fetch(`https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(adresseInput)}&index=StreetAddress&limit=8`,
        { signal: controller.signal })
        .then(r => r.ok ? r.json() : Promise.reject(r))
        .then(json => {
          if (json && json.results) {
            const suggestions = json.results.map((result: any) => ({
              label: result.fulltext,
              houseNumber: result.kind === 'housenumber' ? result.fulltext.split(' ')[0] : '',
              street: result.street,
              postcode: result.zipcode,
              city: result.city
            }));
            setAdresseSuggestions(suggestions);
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Erreur lors de la récupération des adresses:', error);
          }
        });

      return () => controller.abort();
    }, 300);
  }, [adresseInput, adresseEstFrancaise]);

  // Sélection d'une adresse depuis les suggestions IGN
  const handleSelectAdresse = (suggestion: IgnSuggestion) => {
    adresseUserTypingRef.current = false;
    const postcode = suggestion.postcode || '';
    // IGN renvoie `street` type inclus (« Rue d'Athenes »). L'ecrire tel quel dans `nom_voie` sans
    // toucher `type_voie` laissait l'ancien type en place, et l'adresse ressortait « 8 Rue Rue
    // d'Athenes ». On separe donc les deux, comme le fait deja le profil acheteur. Le repli sur
    // `label` est conserve pour les resultats IGN sans `street` (commune seule).
    const { typeVoie, nomVoie } = separerTypeVoie(suggestion.street || suggestion.label);
    setEntrepriseFormData(prev => ({
      ...prev,
      adresse: {
        ...prev.adresse,
        numero_voie: suggestion.houseNumber || '',
        type_voie: typeVoie,
        nom_voie: nomVoie,
        code_postal: postcode,
        ville: suggestion.city || '',
        departement: departementFromCodePostal(postcode) || (prev.adresse?.departement || ''),
      },
    }));
    // `fulltext` IGN vaut deja « voie, code postal ville » ; seul le pays manque. On l'ajoute plutot
    // que de recomposer depuis les morceaux : quand IGN ne renvoie pas `street`, `nom_voie` recoit le
    // fulltext entier, et recomposer dupliquerait alors le code postal et la ville dans la ligne.
    setAdresseInput(adresseUneLigne(suggestion.label, '', '', 'France'));
    setAdresseSuggestions([]);
  };

  // Gestion des changements de champs texte
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    autoSavePres.clearFieldError(name);
  };

  // Effectifs (RH) : entiers bornés à MAX_VALUE. L'input type=number natif suffit.
  const MAX_VALUE = 1_000_000_000;
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const num = value !== '' ? parseFloat(value) : null;
    const clamped = num !== null ? Math.min(MAX_VALUE, Math.max(0, num)) : null;
    setFormData(prev => ({
      ...prev,
      [name]: clamped,
    }));
    autoSavePres.clearFieldError(name);
  };

  // Montants (CA, capital) : entiers (euros). Saisie FR tolérante (virgule/espaces)
  // arrondie à l'euro, montants élevés autorisés, plafond métier ~999 Md.
  // rawMontants garde la frappe brute pendant l'édition (pas de saut de curseur) ;
  // le blur réaffiche la valeur reformatée FR.
  const MONTANT_MAX = 999_999_999_999;
  const [rawMontants, setRawMontants] = useState<Record<string, string>>({});

  const handleMontantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRawMontants(prev => ({ ...prev, [name]: value }));
    const parsed = parseMontantFr(value);
    const clamped = parsed !== null ? Math.min(MONTANT_MAX, Math.max(0, Math.round(parsed))) : null;
    if (name === 'capital') {
      setEntrepriseFormData(prev => ({ ...prev, capital: clamped }));
    } else {
      setFormData(prev => ({ ...prev, [name]: clamped }));
      autoSavePres.clearFieldError(name);
    }
  };

  // Pourcentages (part CA / part RH) : saisie FR décimale, clampage 0-100.
  const handlePercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRawMontants(prev => ({ ...prev, [name]: value }));
    const parsed = parseMontantFr(value);
    const clamped = parsed !== null ? Math.min(100, Math.max(0, parsed)) : null;
    setFormData(prev => ({ ...prev, [name]: clamped }));
    autoSavePres.clearFieldError(name);
  };

  // Au blur : on retire la frappe brute pour réafficher la valeur reformatée FR.
  const handleMontantBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setRawMontants(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // Valeur affichée d'un champ montant/pourcentage : la frappe brute si le champ
  // est en cours d'édition, sinon la valeur numérique reformatée FR.
  const montantDisplay = (name: string, numeric: number | string | null | undefined): string => {
    if (name in rawMontants) return rawMontants[name];
    return formatMontantInput(numeric);
  };

  // Autosave présentation (1500ms debounce)
  const autoSavePres = useAutoSave({
    data: formData,
    saveFn: async (data) => { await updatePresentation(data); },
    delay: 1500,
    enabled: presInitialized,
  });

  // Autosave entreprise (1500ms debounce)
  // L'adresse n'est mise à jour dans entrepriseFormData que lors d'une sélection autocomplete,
  // pas pendant la frappe — donc pas de sauvegarde d'adresse partielle.
  const autoSaveEntreprise = useAutoSave({
    data: entrepriseFormData,
    saveFn: async (data) => { await updateEntreprise(data); },
    delay: 1500,
    enabled: entInitialized,
  });

  // Etats d'autosave (presentation + entreprise) refletes en toast bas a droite.
  useAutoSaveToasts([
    { status: autoSavePres.status, error: autoSavePres.error },
    { status: autoSaveEntreprise.status, error: autoSaveEntreprise.error },
  ]);

  // Erreurs de validation renvoyees par le serveur, par formulaire.
  // Les champs legaux et l'adresse passent par l'autosave entreprise : sans
  // entErrors, une 422 sur RCS ou code postal n'etait affichee nulle part.
  const presErrors = autoSavePres.validationErrors;
  const entErrors = autoSaveEntreprise.validationErrors;

  // Signaler les modifications en attente au contexte
  useEffect(() => {
    const isPending = autoSavePres.status === 'pending' || autoSavePres.status === 'saving'
      || autoSaveEntreprise.status === 'pending' || autoSaveEntreprise.status === 'saving';
    reportAutoSaveStatus('presentation', isPending);
    return () => reportAutoSaveStatus('presentation', false);
  }, [autoSavePres.status, autoSaveEntreprise.status, reportAutoSaveStatus]);

  // Sauvegarde manuelle du SIRET (nécessite validation Sirene)
  const handleSiretSave = async () => {
    const normalizedSiret = siretInput.replace(/[^0-9]/g, '');
    if (!/^\d{14}$/.test(normalizedSiret)) {
      setSiretError('Le SIRET doit contenir exactement 14 chiffres.');
      siretFieldRef.current?.focus();
      return;
    }

    if (normalizedSiret === siretActuel.replace(/[^0-9]/g, '')) {
      setSiretError(null);
      return;
    }

    setSiretChecking(true);
    setSiretError(null);
    try {
      const result = await checkSiret(normalizedSiret);
      if (result.exists) {
        setSiretError('Ce SIRET est déjà utilisé par une autre entité.');
        setSiretChecking(false);
        siretFieldRef.current?.focus();
        return;
      }
    } catch {
      setSiretError('Erreur lors de la vérification du SIRET.');
      setSiretChecking(false);
      return;
    }
    setSiretChecking(false);
    await updateSiret(normalizedSiret);
  };

  // ============================================
  // FRONT-24: Gestion des informations légales
  // ============================================

  // Formater le SIRET avec des espaces
  const formatSiret = (siret?: string | null): string => {
    if (!siret) return '-';
    // Format: 123 456 789 01234
    return siret.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4');
  };

  // Formater l'adresse pour l'affichage (conservé pour uso éventuel)
  const formatAdresse = (adresse?: AdresseData | null): string => {
    if (!adresse) return '-';
    const ligne1 = [adresse.numero_voie, adresse.type_voie, adresse.nom_voie]
      .filter(Boolean)
      .join(' ');
    const ligne2 = `${adresse.code_postal || ''} ${adresse.ville || ''}`.trim();
    const ligne3 = adresse.pays;
    return [ligne1, ligne2, ligne3].filter(Boolean).join('\n');
  };

  // Gestion des changements des champs entreprise
  const handleEntrepriseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (name.startsWith('adresse.')) {
      const adresseField = name.replace('adresse.', '');
      setEntrepriseFormData(prev => {
        const newAdresse = { ...prev.adresse, [adresseField]: value };
        // Le departement se derive d'un code postal FRANCAIS (DOM/Corse pris en charge). Applique a
        // un code postal etranger il fabrique une donnee fausse : "1000" (Bruxelles) donnerait "10",
        // l'Aube. Il est donc recalcule tant que l'adresse est francaise, et remis a vide sinon --
        // y compris quand c'est le pays lui-meme qui vient de changer.
        if (adresseField === 'code_postal' || adresseField === 'pays') {
          newAdresse.departement = estFrance(newAdresse.pays)
            ? departementFromCodePostal(newAdresse.code_postal || '')
            : '';
        }
        return { ...prev, adresse: newAdresse };
      });
    } else if (type === 'checkbox') {
      setEntrepriseFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else if (name === 'date_creation') {
      setEntrepriseFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : null,
      }));
    } else {
      setEntrepriseFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Les handlers entreprise sont appelés directement via handleEntrepriseChange

  // Formatage montant
  const formatMontant = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '-';
    return `${val.toLocaleString('fr-FR')} €`;
  };

  // Formatage effectif
  const formatEffectif = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '-';
    return `${val} salarié${val > 1 ? 's' : ''}`;
  };

  // Formatage pourcentage
  const formatPart = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '';
    return `(${val}% du CA total)`;
  };

  // Vérifier si le prestataire est titulaire sur au moins un lot
  const hasTitulaireLots = (profile?.lots?.lots_departements || []).some(ld => ld.is_locked);

  // Mode unique — formulaire toujours éditable
  return (
    <div className="tab-presentation">
      <div className="presentation-form">
        {/* FRONT-24: Section Informations légales */}
        {/* `overflowVisible` : le champ adresse est desormais le dernier de la section (la rangee code
            postal / ville / pays qui le suivait a ete retiree), sa liste de suggestions IGN depasse
            donc le bas de la carte et se faisait couper par le clip. */}
        <SectionPliable title="Informations légales" overflowVisible>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-entreprise-nom">Nom de l'entreprise</label>
                <input
                  type="text"
                  id="edit-entreprise-nom"
                  value={profile?.entreprise?.nom || ''}
                  disabled
                  className="input-disabled"
                  title="Le nom de l'entreprise ne peut pas être modifié"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-precision">Précision</label>
                <input
                  type="text"
                  id="edit-precision"
                  name="precision"
                  value={entrepriseFormData.precision || ''}
                  onChange={handleEntrepriseChange}
                  placeholder="Ex: (Agence Alpes Côte d'Azur)"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                {canEditSiret ? (
                  <>
                    <label htmlFor="edit-entreprise-siret">SIRET<span className="required-asterisk">*</span></label>
                    <input
                      ref={siretFieldRef}
                      type="text"
                      id="edit-entreprise-siret"
                      className={siretError ? 'input-error' : ''}
                      value={siretInput}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9\s]/g, '');
                        setSiretInput(val);
                        setSiretError(null);
                      }}
                      onBlur={handleSiretSave}
                      placeholder="Ex: 123 456 789 01234"
                      maxLength={17}
                    />
                    <span className="char-count">{siretInput.replace(/[^0-9]/g, '').length}/14 chiffres</span>
                    {siretError && <span className="field-error">{siretError}</span>}
                    {siretChecking && <span className="field-info">Vérification en cours...</span>}
                  </>
                ) : (
                  <>
                    <label htmlFor="edit-entreprise-siret">SIRET<span className="required-asterisk">*</span></label>
                    <input
                      type="text"
                      id="edit-entreprise-siret"
                      value={formatSiret(profile?.entreprise?.siret)}
                      disabled
                      className="input-disabled"
                      title="Le SIRET ne peut pas être modifié"
                    />
                  </>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="edit-RCS">RCS<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="edit-RCS"
                  name="RCS"
                  value={entrepriseFormData.RCS || ''}
                  onChange={handleEntrepriseChange}
                  className={classeChamp('RCS', !!entErrors?.RCS)}
                  placeholder="Ex: Paris B 123 456 789"
                />
              </div>
            </div>

            <div className="form-row three-cols">
              <div className="form-group">
                <label htmlFor="edit-statut_juridique">Statut juridique<span className="required-asterisk">*</span></label>
                <select
                  id="edit-statut_juridique"
                  name="statut_juridique"
                  value={entrepriseFormData.statut_juridique || ''}
                  onChange={handleEntrepriseChange}
                  className={classeChamp('statut_juridique', !!entErrors?.statut_juridique)}
                >
                  <option value="">Sélectionner...</option>
                  {FORMES_JURIDIQUES.map(forme => (
                    <option key={forme} value={forme}>{forme}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="edit-capital">Capital (€)<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  inputMode="decimal"
                  id="edit-capital"
                  name="capital"
                  value={montantDisplay('capital', entrepriseFormData.capital)}
                  onChange={handleMontantChange}
                  onBlur={handleMontantBlur}
                  className={classeChamp('capital', !!entErrors?.capital)}
                  placeholder="Ex: 10 000"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-date_creation">Année de création<span className="required-asterisk">*</span></label>
                <input
                  type="number"
                  id="edit-date_creation"
                  name="date_creation"
                  value={entrepriseFormData.date_creation ?? ''}
                  onChange={handleEntrepriseChange}
                  className={classeChamp('date_creation', !!entErrors?.date_creation)}
                  min="1800"
                  max={currentYear}
                  placeholder="Ex: 2015"
                />
              </div>
            </div>

            {/* Redressement judiciaire */}
            <div className="form-group">
              <label htmlFor="edit-redressement-switch" className="redressement-label">
                Redressement judiciaire<span className="required-asterisk">*</span>
              </label>
              <div className="redressement-label-block redressement-section">
                <div className="redressement-description">
                  L'entreprise est-elle en redressement ?
                </div>
                <div className="redressement-switch-control">
                  <Switch
                    id="edit-redressement-switch"
                    isOn={entrepriseFormData.redressement_judiciaire || false}
                    handleToggle={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEntrepriseFormData(prev => ({ ...prev, redressement_judiciaire: e.target.checked }))
                    }
                    onColor="#f97316"
                  />
                </div>
              </div>
            </div>

            {/* Pays du siege. Il commande le reste du bloc adresse, il est donc affiche dans les
                deux cas : sans lui, une entreprise inscrite avec le mauvais pays n'avait aucun
                moyen de le corriger, et une entreprise etrangere restait prisonniere d'un champ
                d'adresse adosse a un geocodeur qui ne connait que la France. */}
            <div className="form-row">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="edit-adresse-pays">Pays<span className="required-asterisk">*</span></label>
                <select
                  id="edit-adresse-pays"
                  name="adresse.pays"
                  value={paysAdresse}
                  onChange={handleEntrepriseChange}
                  className={classeChamp('adresse_pays', !!entErrors?.['adresse.pays'])}
                >
                  {!paysEstCodeConnu && (
                    <option value={paysAdresse}>{paysAdresse || 'Sélectionnez...'}</option>
                  )}
                  <optgroup label="Europe">
                    {PAYS_EUROPE.map(p => (
                      <option key={p.code} value={p.code}>{p.nom}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Reste du monde">
                    {PAYS_HORS_EUROPE.map(p => (
                      <option key={p.code} value={p.code}>{p.nom}</option>
                    ))}
                  </optgroup>
                </select>
                {entErrors?.['adresse.pays'] && <span className="field-error">{entErrors['adresse.pays'][0]}</span>}
              </div>
            </div>

            {adresseEstFrancaise ? (
            <>
            <div className="form-row">
              <div className="form-group adresse-autocomplete-container" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="edit-adresse-search">Adresse<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="edit-adresse-search"
                  name="adresse_search"
                  value={adresseInput}
                  onChange={(e) => {
                    adresseUserTypingRef.current = true;
                    setAdresseInput(e.target.value);
                    // Ne pas mettre à jour entrepriseFormData ici : l'autosave
                    // ne se déclenchera qu'à la sélection dans le dropdown.
                  }}
                  // Cle serveur adresse_voie : ce champ de recherche est le seul
                  // point de saisie de nom_voie, renseigne via le dropdown IGN.
                  className={classeChamp('adresse_voie', !!entErrors?.['adresse.nom_voie'])}
                  placeholder="Ex: 12 rue de la Paix, 75001 Paris"
                  autoComplete="off"
                />
                {adresseSuggestions.length > 0 && (
                  <ul className="autocomplete-list adresse-suggestions">
                    {adresseSuggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectAdresse(suggestion);
                        }}
                        className="adresse-suggestion-item"
                      >
                        {suggestion.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Adresse francaise : code postal, ville et pays sont DERIVES de la suggestion IGN
                choisie, plus affiches. Ils restent dans `entrepriseFormData.adresse` et partent en
                base comme avant — seul le rendu change, l'adresse tenant sur une seule ligne. La
                regle « IGN uniquement » etait deja en place ici : taper dans le champ de recherche
                n'ecrit rien, seule la selection dans le dropdown met a jour (cf.
                handleSelectAdresse). Les masquer n'a donc demande aucun changement de logique. */}
            </>
            ) : (
            /* Adresse etrangere : aucun geocodeur ne la couvre, tous les champs sont donc saisis en
               clair. C'est le seul chemin par lequel une entreprise hors de France peut corriger son
               adresse : le champ de recherche IGN, lui, n'ecrit rien tant qu'aucune suggestion n'est
               choisie, et aucune suggestion etrangere n'existe. */
            <>
            <div className="form-row">
              <div className="form-group" style={{ flex: '0 0 120px' }}>
                <label htmlFor="edit-adresse-numero">Numéro</label>
                <input
                  type="text"
                  id="edit-adresse-numero"
                  name="adresse.numero_voie"
                  value={entrepriseFormData.adresse?.numero_voie || ''}
                  onChange={handleEntrepriseChange}
                  placeholder="Ex: 12"
                  maxLength={10}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-adresse-voie">Rue / voie<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="edit-adresse-voie"
                  name="adresse.nom_voie"
                  value={entrepriseFormData.adresse?.nom_voie || ''}
                  onChange={handleEntrepriseChange}
                  className={classeChamp('adresse_voie', !!entErrors?.['adresse.nom_voie'])}
                  placeholder="Ex: Rue de la Loi, Hauptstrasse..."
                  maxLength={255}
                />
                {entErrors?.['adresse.nom_voie'] && <span className="field-error">{entErrors['adresse.nom_voie'][0]}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-adresse-cp">Code postal<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="edit-adresse-cp"
                  name="adresse.code_postal"
                  value={entrepriseFormData.adresse?.code_postal || ''}
                  onChange={handleEntrepriseChange}
                  className={classeChamp('adresse_code_postal', !!entErrors?.['adresse.code_postal'])}
                  placeholder="Ex: 1000, 1012 AB, 00-950"
                  maxLength={12}
                />
                {entErrors?.['adresse.code_postal'] && <span className="field-error">{entErrors['adresse.code_postal'][0]}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="edit-adresse-ville">Ville<span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="edit-adresse-ville"
                  name="adresse.ville"
                  value={entrepriseFormData.adresse?.ville || ''}
                  onChange={handleEntrepriseChange}
                  className={classeChamp('adresse_ville', !!entErrors?.['adresse.ville'])}
                  placeholder="Ex: Bruxelles"
                  maxLength={100}
                />
                {entErrors?.['adresse.ville'] && <span className="field-error">{entErrors['adresse.ville'][0]}</span>}
              </div>
            </div>
            </>
            )}
        </SectionPliable>

        {/* Section 1: Description de l'entreprise */}
        <SectionPliable title="Description de l'entreprise">
            <div className="form-group full-width">
              <label htmlFor="presentation_entreprise">Présentation de l'entreprise<span className="required-asterisk">*</span></label>
              <textarea
                ref={fitTextarea}
                id="presentation_entreprise"
                name="presentation_entreprise"
                value={formData.presentation_entreprise || ''}
                onChange={handleChange}
                className={`auto-grow ${classeChamp('presentation_entreprise', !!presErrors?.presentation_entreprise)}`.trim()}
                rows={4}
                maxLength={3000}
                placeholder="Ex: Nous sommes une entreprise spécialisée dans la maîtrise d'œuvre..."
              />
              {presErrors?.presentation_entreprise && <span className="field-error">{presErrors.presentation_entreprise[0]}</span>}
              <span className="char-counter">{(formData.presentation_entreprise || '').length}/3000</span>
            </div>

            <div className="form-group full-width">
              <label htmlFor="presentation_humain">Présentation des moyens humains et matériel utilisés en général pour vos opérations<span className="required-asterisk">*</span></label>
              <textarea
                ref={fitTextarea}
                id="presentation_humain"
                name="presentation_humain"
                value={formData.presentation_humain || ''}
                onChange={handleChange}
                className={`auto-grow ${classeChamp('presentation_humain', !!presErrors?.presentation_humain)}`.trim()}
                rows={3}
                maxLength={3000}
                placeholder="Ex: Notre équipe de 15 personnes dispose de..."
              />
              {presErrors?.presentation_humain && <span className="field-error">{presErrors.presentation_humain[0]}</span>}
              <span className="char-counter">{(formData.presentation_humain || '').length}/3000</span>
            </div>

            <div className="form-group full-width">
              <label htmlFor="presentation_operationnel">Présentation des modes opératoires utilisés en général pour vos opérations<span className="required-asterisk">*</span></label>
              <textarea
                ref={fitTextarea}
                id="presentation_operationnel"
                name="presentation_operationnel"
                value={formData.presentation_operationnel || ''}
                onChange={handleChange}
                className={`auto-grow ${classeChamp('presentation_operationnel', !!presErrors?.presentation_operationnel)}`.trim()}
                rows={3}
                maxLength={3000}
                placeholder="Ex: Notre process de travail en 3 étapes..."
              />
              {presErrors?.presentation_operationnel && <span className="field-error">{presErrors.presentation_operationnel[0]}</span>}
              <span className="char-counter">{(formData.presentation_operationnel || '').length}/3000</span>
            </div>

            <div className="form-group full-width">
              <label htmlFor="presentation_environnement">Engagements environnementaux et sociaux<span className="required-asterisk">*</span></label>
              <textarea
                ref={fitTextarea}
                id="presentation_environnement"
                name="presentation_environnement"
                value={formData.presentation_environnement || ''}
                onChange={handleChange}
                className={`auto-grow ${classeChamp('presentation_environnement', !!presErrors?.presentation_environnement)}`.trim()}
                rows={3}
                maxLength={3000}
                placeholder="Ex: Certifiés ISO 14001, nous nous engageons à..."
              />
              {presErrors?.presentation_environnement && <span className="field-error">{presErrors.presentation_environnement[0]}</span>}
              <span className="char-counter">{(formData.presentation_environnement || '').length}/3000</span>
            </div>
        </SectionPliable>

        {/* Section 2: Capacités économiques et financières */}
        <SectionPliable title="Capacités économiques et financières">
            <p className="section-description">
              Chiffre d'affaires global annuel en € et Part du CA des prestations similaires (%)
            </p>
            <div className="capacites-grid ca-grid">
              <div className="form-group">
                <label>(N-1)<span className="required-asterisk">*</span></label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="ca_n1"
                    name="ca_n1"
                    value={montantDisplay('ca_n1', formData.ca_n1)}
                    onChange={handleMontantChange}
                    onBlur={handleMontantBlur}
                    className={classeChamp('ca_n1', !!presErrors?.ca_n1)}
                    placeholder="Ex: 500 000"
                  />
                  <span className="suffix">€</span>
                </div>
                {presErrors?.ca_n1 && <span className="field-error">{presErrors.ca_n1[0]}</span>}
              </div>
              <div className="form-group">
                <label>Part du CA (N-1)<span className="required-asterisk">*</span></label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="part_ca_n1"
                    name="part_ca_n1"
                    value={montantDisplay('part_ca_n1', formData.part_ca_n1)}
                    onChange={handlePercentChange}
                    onBlur={handleMontantBlur}
                    className={classeChamp('part_ca_n1', !!presErrors?.part_ca_n1)}
                    placeholder="Ex: 35"
                  />
                  <span className="suffix">%</span>
                </div>
                {presErrors?.part_ca_n1 && <span className="field-error">{presErrors.part_ca_n1[0]}</span>}
              </div>
              <div className="form-group">
                <label>(N-2)<span className="required-asterisk">*</span></label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="ca_n2"
                    name="ca_n2"
                    value={montantDisplay('ca_n2', formData.ca_n2)}
                    onChange={handleMontantChange}
                    onBlur={handleMontantBlur}
                    className={classeChamp('ca_n2', !!presErrors?.ca_n2)}
                    placeholder="Ex: 450 000"
                  />
                  <span className="suffix">€</span>
                </div>
                {presErrors?.ca_n2 && <span className="field-error">{presErrors.ca_n2[0]}</span>}
              </div>
              <div className="form-group">
                <label>Part du CA (N-2)<span className="required-asterisk">*</span></label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="part_ca_n2"
                    name="part_ca_n2"
                    value={montantDisplay('part_ca_n2', formData.part_ca_n2)}
                    onChange={handlePercentChange}
                    onBlur={handleMontantBlur}
                    className={classeChamp('part_ca_n2', !!presErrors?.part_ca_n2)}
                    placeholder="Ex: 30"
                  />
                  <span className="suffix">%</span>
                </div>
                {presErrors?.part_ca_n2 && <span className="field-error">{presErrors.part_ca_n2[0]}</span>}
              </div>
              <div className="form-group">
                <label>(N-3)<span className="required-asterisk">*</span></label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="ca_n3"
                    name="ca_n3"
                    value={montantDisplay('ca_n3', formData.ca_n3)}
                    onChange={handleMontantChange}
                    onBlur={handleMontantBlur}
                    className={classeChamp('ca_n3', !!presErrors?.ca_n3)}
                    placeholder="Ex: 400 000"
                  />
                  <span className="suffix">€</span>
                </div>
                {presErrors?.ca_n3 && <span className="field-error">{presErrors.ca_n3[0]}</span>}
              </div>
              <div className="form-group">
                <label>Part du CA (N-3)<span className="required-asterisk">*</span></label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="part_ca_n3"
                    name="part_ca_n3"
                    value={montantDisplay('part_ca_n3', formData.part_ca_n3)}
                    onChange={handlePercentChange}
                    onBlur={handleMontantBlur}
                    className={classeChamp('part_ca_n3', !!presErrors?.part_ca_n3)}
                    placeholder="Ex: 25"
                  />
                  <span className="suffix">%</span>
                </div>
                {presErrors?.part_ca_n3 && <span className="field-error">{presErrors.part_ca_n3[0]}</span>}
              </div>
            </div>
        </SectionPliable>

        {/* Section 3: Capacités techniques et professionnelles */}
        <SectionPliable title="Capacités techniques et professionnelles">
            <p className="section-description">
              Effectifs moyens annuels et Part du personnel d'encadrement (%)
            </p>
            <div className="capacites-grid rh-grid">
              <div className="form-group">
                <label>ETP (N-1)<span className="required-asterisk">*</span></label>
                <input
                  type="number"
                  id="rh_n1"
                  name="rh_n1"
                  value={formData.rh_n1 ?? ''}
                  onChange={handleNumberChange}
                  className={classeChamp('rh_n1', !!presErrors?.rh_n1)}
                  min="0"
                  max="1000000000"
                  placeholder="Ex: 15"
                />
                {presErrors?.rh_n1 && <span className="field-error">{presErrors.rh_n1[0]}</span>}
              </div>
              <div className="form-group">
                <label>Part ETP (N-1)<span className="required-asterisk">*</span></label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="part_rh_n1"
                    name="part_rh_n1"
                    value={montantDisplay('part_rh_n1', formData.part_rh_n1)}
                    onChange={handlePercentChange}
                    onBlur={handleMontantBlur}
                    className={classeChamp('part_rh_n1', !!presErrors?.part_rh_n1)}
                    placeholder="Ex: 20"
                  />
                  <span className="suffix">%</span>
                </div>
                {presErrors?.part_rh_n1 && <span className="field-error">{presErrors.part_rh_n1[0]}</span>}
              </div>
              <div className="form-group">
                <label>ETP (N-2)<span className="required-asterisk">*</span></label>
                <input
                  type="number"
                  id="rh_n2"
                  name="rh_n2"
                  value={formData.rh_n2 ?? ''}
                  onChange={handleNumberChange}
                  className={classeChamp('rh_n2', !!presErrors?.rh_n2)}
                  min="0"
                  max="1000000000"
                  placeholder="Ex: 12"
                />
                {presErrors?.rh_n2 && <span className="field-error">{presErrors.rh_n2[0]}</span>}
              </div>
              <div className="form-group">
                <label>Part ETP (N-2)<span className="required-asterisk">*</span></label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="part_rh_n2"
                    name="part_rh_n2"
                    value={montantDisplay('part_rh_n2', formData.part_rh_n2)}
                    onChange={handlePercentChange}
                    onBlur={handleMontantBlur}
                    className={classeChamp('part_rh_n2', !!presErrors?.part_rh_n2)}
                    placeholder="Ex: 18"
                  />
                  <span className="suffix">%</span>
                </div>
                {presErrors?.part_rh_n2 && <span className="field-error">{presErrors.part_rh_n2[0]}</span>}
              </div>
              <div className="form-group">
                <label>ETP (N-3)<span className="required-asterisk">*</span></label>
                <input
                  type="number"
                  id="rh_n3"
                  name="rh_n3"
                  value={formData.rh_n3 ?? ''}
                  onChange={handleNumberChange}
                  className={classeChamp('rh_n3', !!presErrors?.rh_n3)}
                  min="0"
                  max="1000000000"
                  placeholder="Ex: 10"
                />
                {presErrors?.rh_n3 && <span className="field-error">{presErrors.rh_n3[0]}</span>}
              </div>
              <div className="form-group">
                <label>Part ETP (N-3)<span className="required-asterisk">*</span></label>
                <div className="input-with-suffix">
                  <input
                    type="text"
                    inputMode="decimal"
                    id="part_rh_n3"
                    name="part_rh_n3"
                    value={montantDisplay('part_rh_n3', formData.part_rh_n3)}
                    onChange={handlePercentChange}
                    onBlur={handleMontantBlur}
                    className={classeChamp('part_rh_n3', !!presErrors?.part_rh_n3)}
                    placeholder="Ex: 15"
                  />
                  <span className="suffix">%</span>
                </div>
                {presErrors?.part_rh_n3 && <span className="field-error">{presErrors.part_rh_n3[0]}</span>}
              </div>
            </div>
        </SectionPliable>

        {/* Section 4: Perimetre geographique prestataire libre (carte editable) */}
        {profilType.showLots && (
          <SectionPliable className="lots-section" title={<>Périmètre géographique d'intervention en tant que prestataire libre{!hasTitulaireLots && <span className="required-asterisk">*</span>}</>}>
              <PrestataireZonesCarte mode="libre" />
          </SectionPliable>
        )}

        {/* Section 5: Perimetre geographique prestataire titulaire (carte lecture seule) */}
        {profilType.showLots && (
          <SectionPliable className="lots-section" title="Périmètre géographique d'intervention en tant que prestataire titulaire de l'accord cadre 2025-2026">
              <PrestataireZonesCarte mode="ac" />
          </SectionPliable>
        )}
      </div>
    </div>
  );
};

export default TabPresentation;
